import { createSelector } from '@ngrx/store';
import {
  AccountPaymentMethod,
  BurnToken,
  PaymentPreviewPayableV1Response,
  PaymentPreviewTransactionV1Response,
  RedirectStateOneTimePaymentAccount,
  roundTo2Decimals,
  TwikeyExitStatus,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  selectExternalUserUrn,
  selectIsPaymentLinks,
  selectTotalAmount,
} from '../../session-store/selectors/session.selectors';
import { selectAllowBuyerLogin } from '../../payment-rules/selectors/payment-rules.selectors';
import {
  PayStore,
  selectPadAgreementFormSubmitEvent,
  selectRedirectSelectedPaymentAccountBurn,
  selectTwikeyExitStatus,
} from '../reducers/btpay.reducers';
import { isToday } from 'date-fns';

export const selectSessionPaymentAccount = createSelector(PayStore, (state) => {
  return state?.selectedPaymentAccount;
});

export const selectPaymentDate = createSelector(PayStore, (state) => {
  return state.paymentDate;
});

export const selectEmailRecipients = createSelector(PayStore, (state) => {
  return state.emailRecipients;
});

export const selectReceiptRecipients = createSelector(PayStore, (state) => {
  return state.emailRecipients;
});

export const selectPaymentPreview = createSelector(PayStore, (state) => {
  return state.paymentPreview;
});

export const selectIsAllPayablesPaidInFull = createSelector(
  PayStore,
  (state) => state?.isAllPayablesPaidInFull,
);

export const selectPaymentReference = createSelector(PayStore, (state) => {
  if (state.paymentPreview == null) {
    return null;
  }

  // payment reference is repeated for all payments in the same session
  return state.paymentPreview.payments[0].paymentReference;
});

export const selectSingleTransactionReferenceNumber = createSelector(
  PayStore,
  (state) => {
    if (state.paymentPreview == null) {
      return null;
    }

    const burn = BurnToken.parse(
      state.paymentPreview.payments[0].payment.transactionReference.burn,
    );
    return burn.propertyValue;
  },
);

export const selectPaymentAccounts = createSelector(
  PayStore,
  (store) =>
    store.paymentAccounts?.slice().sort((a, b) => {
      if (a.paymentAccountIdentifierBurn < b.paymentAccountIdentifierBurn) {
        return -1;
      }
      if (a.paymentAccountIdentifierBurn > b.paymentAccountIdentifierBurn) {
        return 1;
      }
      return 0;
    }),
);

export const selectHasPaymentAccounts = createSelector(
  selectPaymentAccounts,
  (accounts) => accounts?.length > 0,
);

export const selectBuyerAccountIdentifiers = createSelector(
  PayStore,
  (state) => {
    if (state.paymentPreview == null) {
      return null;
    }

    return state.paymentPreview.payments.map(
      (x) => x.account?.buyerAccountIdentifier,
    );
  },
);

export const selectPaymentReferenceBurn = createSelector(
  selectPaymentReference,
  (paymentReference) => {
    if (paymentReference == null) {
      return null;
    }

    return paymentReference.burn;
  },
);

export const selectPaymentReferenceNumber = createSelector(
  selectPaymentReferenceBurn,
  (paymentReference) => {
    if (paymentReference == null || paymentReference === '') {
      return null;
    }

    const burn = BurnToken.parse(paymentReference);
    return burn.propertyValue;
  },
);

export const selectPaymentReferenceDetails = createSelector(
  selectPaymentReference,
  selectPaymentReferenceNumber,
  (paymentReference, paymentReferenceNumber) => {
    return {
      burn: paymentReference?.burn ?? null,
      value: paymentReferenceNumber,
    };
  },
);

export const selectTransactionAuthorizationResults = createSelector(
  PayStore,
  (state) => {
    return state?.transactionAuthorizationResults;
  },
);

export const selectTransactionAuthorizationResultsList = createSelector(
  PayStore,
  (state) => {
    return Object.values(state?.transactionAuthorizationResults ?? {});
  },
);

export const selectIsScheduledPayment = createSelector(
  selectTransactionAuthorizationResultsList,
  (state) => {
    return state.some((x) => x.isScheduledTransaction);
  },
);

export const selectPaymentInitiator = createSelector(
  selectPaymentPreview,
  (state) => {
    // payment initiator is repeated for all payments in the same session
    return state.payments[0].paymentInitiator;
  },
);

export const selectPaymentPreviewPaymentsMap = createSelector(
  selectPaymentPreview,
  (state) => {
    if (
      state.payments.some(
        (p) =>
          p.payment.transactionReference == null ||
          p.payment.transactionReference.burn === '',
      )
    ) {
      return new Map<string, PaymentPreviewTransactionV1Response>();
    }

    return new Map<string, PaymentPreviewTransactionV1Response>(
      state.payments.map((payment) => [
        BurnToken.parse(payment.payment.transactionReference.burn)
          .propertyValue,
        payment.payment,
      ]),
    ) as ReadonlyMap<string, PaymentPreviewTransactionV1Response>;
  },
);

export const selectAuthorizedPaymentDate = createSelector(
  selectTransactionAuthorizationResultsList,
  (results) => {
    // Payment date will be the same one for all transactions, so we just need to take it from the first successful one
    const transactionResult = results.find((r) => r.paymentDate != null);

    if (transactionResult == null) {
      return null;
    }

    const { paymentDate } = transactionResult;

    return paymentDate;
  },
);

export const selectAuthorizePaymentError = createSelector(
  selectTransactionAuthorizationResultsList,
  (results) => {
    const transactionResult = results.find((r) => r.errorMessage != null);

    if (transactionResult == null) {
      return null;
    }

    return transactionResult.errorMessage;
  },
);

export const selectPaymentPreviewErrors = createSelector(
  selectPaymentPreview,
  (paymentPreview) => {
    if (paymentPreview == null) {
      return null;
    }

    return paymentPreview.errors;
  },
);

export const selectPaymentPreviewPayables = createSelector(
  selectPaymentPreview,
  (paymentPreview) => {
    if (paymentPreview == null) {
      return null;
    }
    let payables: PaymentPreviewPayableV1Response[] = [];
    paymentPreview.payments.forEach((p) => {
      payables = payables.concat(p.payment.payables);
    });
    return payables;
  },
);

export const selectTotalSurcharge = createSelector(
  selectPaymentPreview,
  (paymentPreview) => {
    if (paymentPreview == null) {
      return null;
    }

    return roundTo2Decimals(
      paymentPreview.payments
        .map((p) => p.payment.totalSurcharge)
        .reduce((acc, curr) => acc + curr, 0),
    );
  },
);

export const selectPaymentPreviewTotalAmount = createSelector(
  selectTotalAmount,
  selectTotalSurcharge,
  (subTotal, surchargeSubTotal) => {
    return roundTo2Decimals(subTotal + (surchargeSubTotal ?? 0));
  },
);

export const selectIsAutopayEnabled = createSelector(PayStore, (state) => {
  return state?.isAutopayEnabled;
});

export const selectPaymentAuthorizationPending = createSelector(
  PayStore,
  (state) => {
    return state?.paymentAuthorizationPending;
  },
);

export const selectPaymentStatusCheckCount = createSelector(
  PayStore,
  (state) => {
    return state?.paymentStatusCheckCount;
  },
);

export const selectPaymentAuthorizing = createSelector(PayStore, (state) => {
  return state?.paymentAuthorizing;
});

export const selectPaymentAuthorizationTimedOut = createSelector(
  PayStore,
  (state) => {
    return state.paymentAuthorizing && state.paymentAuthorizationPending;
  },
);

export const selectPaymentStatusCheckTimedOut = createSelector(
  PayStore,
  (state) => {
    return state?.paymentStatusCheckTimedOut;
  },
);

export const selectBuyerAccounts = createSelector(PayStore, (state) => {
  return state.buyerAccounts;
});

export const selectPayOtherAmountHasThrownError = createSelector(
  PayStore,
  (state) => {
    return !!state.payOtherAmount?.errors?.length;
  },
);

export const selectBankHolidayDates = createSelector(PayStore, (state) => {
  return state.bankHolidays?.map((x) => x.date);
});

export const selectAchPaymentDateWarningVerbiage = createSelector(
  PayStore,
  (state) => {
    return state.achPaymentDateWarningVerbiage;
  },
);

export const selectShowAchPaymentDateWarning = createSelector(
  PayStore,
  (state) => {
    return state.showAchPaymentDateWarning;
  },
);

export const selectIsScheduledTransaction = createSelector(
  selectSessionPaymentAccount,
  selectPaymentDate,
  (paymentAccount, paymentDate) => {
    return (
      paymentAccount.paymentMethod === AccountPaymentMethod.Ach ||
      !isToday(paymentDate)
    );
  },
);

export const selectBankAccountBurns = createSelector(
  selectPaymentAccounts,
  (paymentAccounts) => {
    return paymentAccounts
      ?.filter(
        (paymentAccount) =>
          paymentAccount?.paymentMethod == AccountPaymentMethod.Ach,
      )
      .map((paymentAccount) => paymentAccount.paymentAccountIdentifierBurn);
  },
);

export const selectNonRedirectBankAccountBurns = createSelector(
  selectBankAccountBurns,
  selectRedirectSelectedPaymentAccountBurn,
  (paymentAccounts, redirectSelectedPaymentAccountBurn) => {
    return paymentAccounts?.filter(
      (paymentAccountIdentifierBurn) =>
        paymentAccountIdentifierBurn !== redirectSelectedPaymentAccountBurn,
    );
  },
);

export const selectHasBankAccounts = createSelector(
  selectBankAccountBurns,
  (bankAccountBurns) => bankAccountBurns?.length > 0,
);

export const selectHasNonSelectedBankAccounts = createSelector(
  selectNonRedirectBankAccountBurns,
  (bankAccountBurns) => bankAccountBurns?.length > 0,
);

export const selectRedirectOneTimePaymentAccounts = createSelector(
  selectPaymentAccounts,
  (paymentAccounts) => {
    return paymentAccounts
      ?.filter((paymentAccount) => paymentAccount?.isOneTimePaymentAccount)
      .map((oneTimePaymentAccount) => {
        const paymentAccount: RedirectStateOneTimePaymentAccount = {
          paymentAccountIdentifierBurn:
            oneTimePaymentAccount.paymentAccountIdentifierBurn,
          displayName: oneTimePaymentAccount.displayName,
          paymentMethod: oneTimePaymentAccount.paymentMethod,
          accountLast4: oneTimePaymentAccount.accountLast4,
          ...(oneTimePaymentAccount.paymentMethod ===
            AccountPaymentMethod.Card && {
            nameOnCard: oneTimePaymentAccount.nameOnCard,
            expirationYear: oneTimePaymentAccount.expirationYear,
            expirationMonth: oneTimePaymentAccount.expirationMonth,
            cardNetwork: oneTimePaymentAccount.cardNetwork,
            createdWithCardOverride:
              oneTimePaymentAccount.createdWithCardOverride,
            shouldRequestCvv: oneTimePaymentAccount.shouldRequestCvv,
          }),
          ...(oneTimePaymentAccount.paymentMethod ===
            AccountPaymentMethod.Ach && {
            bankName: oneTimePaymentAccount.bankName,
            nameOnBankAccount: oneTimePaymentAccount.nameOnBankAccount,
          }),
        };

        return paymentAccount;
      });
  },
);

export const selectShouldRedirectToTwikeyForPadSignature = createSelector(
  selectPadAgreementFormSubmitEvent,
  (padAgreementFormSubmitEvent) => padAgreementFormSubmitEvent != null,
);

export const selectPayorDetail = createSelector(
  selectPadAgreementFormSubmitEvent,
  (padAgreementFormSubmitEvent) =>
    padAgreementFormSubmitEvent?.value?.payorDetails,
);

export const selectPayorBankAccountDetail = createSelector(
  selectPadAgreementFormSubmitEvent,
  (padAgreementFormSubmitEvent) =>
    padAgreementFormSubmitEvent?.value?.payorBankAccountDetail,
);

export const selectIsTwikeyStatusSet = createSelector(
  selectTwikeyExitStatus,
  (twikeyStatus) => twikeyStatus != null,
);

export const selectIsTwikeyStatusNotOk = createSelector(
  selectIsTwikeyStatusSet,
  selectTwikeyExitStatus,
  (twikeyStatusSet, twikeyStatus) =>
    twikeyStatusSet && twikeyStatus != TwikeyExitStatus.Ok,
);

export const selectStripeAccountId = createSelector(
  selectPaymentPreview,
  (preview) => preview?.payments[0].depositoryAccount?.merchantAccountToken,
);

/* istanbul ignore next */
export const selectSignInBannerDismissed = createSelector(
  PayStore,
  (state) => !!state?.signInBannerDismissed,
);

/* istanbul ignore next */
export const selectSignInEmailToken = createSelector(
  PayStore,
  (state) => state?.signInEmailToken ?? null,
);

/* istanbul ignore next */
export const selectSignInUsername = createSelector(
  PayStore,
  (state) => state?.signInUsername ?? null,
);

/* istanbul ignore next */
export const selectSignInEmailSentBannerVisible = createSelector(
  PayStore,
  (state) => !!state?.signInEmailSentBannerVisible,
);

/* istanbul ignore next */
export const selectSignInEmailErrorOccurred = createSelector(
  PayStore,
  (state) => !!state?.signInEmailErrorOccurred,
);

export const selectShowSignInBanner = createSelector(
  selectIsPaymentLinks,
  selectSignInBannerDismissed,
  selectSignInEmailSentBannerVisible,
  selectExternalUserUrn,
  selectAllowBuyerLogin,
  (
    isPaymentLinks,
    dismissed,
    emailSentBannerVisible,
    externalUserUrn,
    allowBuyerLogin,
  ) =>
    isPaymentLinks &&
    !dismissed &&
    !emailSentBannerVisible &&
    !externalUserUrn &&
    allowBuyerLogin,
);

export const selectShowSignInEmailSentBanner = createSelector(
  selectIsPaymentLinks,
  selectSignInBannerDismissed,
  selectSignInEmailSentBannerVisible,
  selectAllowBuyerLogin,
  (isPaymentLinks, dismissed, emailSentBannerVisible, allowBuyerLogin) =>
    isPaymentLinks && !dismissed && emailSentBannerVisible && allowBuyerLogin,
);

/* istanbul ignore next */
export const selectIssuePlatformJwtError = createSelector(
  PayStore,
  (state) => !!state?.issuePlatformJwtError,
);

export const selectShowIssuePlatformJwtError = createSelector(
  selectShowSignInBanner,
  selectIssuePlatformJwtError,
  selectAllowBuyerLogin,
  (showSignInBanner, issuePlatformJwtError, allowBuyerLogin) =>
    showSignInBanner && issuePlatformJwtError && allowBuyerLogin,
);

export const selectEmailVerificationPending = createSelector(
  selectSignInEmailSentBannerVisible,
  selectSignInEmailErrorOccurred,
  (visible, errored) => visible && !errored,
);

/* istanbul ignore next */
export const selectDisablePaymentDate = createSelector(
  PayStore,
  (state) => !!state?.disablePaymentDate,
);

/* istanbul ignore next */
export const selectSignInAttemptsExceeded = createSelector(
  PayStore,
  (state) => state?.signInAttemptsExceeded ?? false,
);

// BTPAY-7160: per-account eligibility check result, dispatched by each flow's own container
// (PoiPaymentConfirmationComponentActions/PoaPaymentSummaryComponentActions/
// PoiIntlMakePaymentComponentActions checkAutopayEligibility) via createCheckAutopayEligibilityEffect.
export const selectUnenrolledAutopayAccounts = createSelector(
  PayStore,
  (state) => state?.unenrolledAutopayAccounts,
);

export const selectHasEligibleAutopayAccounts = createSelector(
  selectUnenrolledAutopayAccounts,
  (unenrolledAccounts) => unenrolledAccounts?.length > 0,
);
